import React, { useEffect, useState } from 'react'

import {
  CreateCommentBody,
  TCommentThread,
  TCommentThreadData
} from 'types/GlobalComment'

import { useTranslation } from 'language/translator'

import Button from 'components_new/atoms/Button'
import Icon from 'components_new/atoms/Icon'

import CommentThread, {
  CommentThreadProps
} from 'components_new/molecules/CommentThread'
import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'

interface Props {
  analysisId?: string | null
  CommentThreadProps?: Pick<
    CommentThreadProps,
    'border' | 'disabledPadding' | 'displayHeader'
  >
  commentThreads: TCommentThread[]
  focusedCommentThreadId?: string | null
  keyMetricId?: string | null
  noCommentsBody?: string
  onAddComment?: () => void
  widgetId?: string | null
  newCommentThread?: TCommentThreadData | null
  onCreateComment: (body: CreateCommentBody) => void
  setFocusedCommentThreadId?: React.Dispatch<
    React.SetStateAction<string | null>
  >
  setNewCommentThread?: React.Dispatch<
    React.SetStateAction<TCommentThreadData | null>
  >
}

const CommentThreads = (props: Props) => {
  const {
    CommentThreadProps,
    commentThreads,
    focusedCommentThreadId,
    analysisId,
    keyMetricId,
    noCommentsBody,
    onAddComment,
    widgetId,
    newCommentThread,
    onCreateComment,
    setFocusedCommentThreadId,
    setNewCommentThread
  } = props

  const { t } = useTranslation()

  const [newCommentThreadActive, setNewCommentThreadActive] =
    useState<boolean>(false)

  const newCommentThreadInputRef = React.useRef<
    HTMLInputElement | HTMLTextAreaElement | null
  >(null)

  useEffect(() => {
    if (newCommentThread) {
      const activeTimer = setTimeout(() => {
        setNewCommentThreadActive(true)
      }, 200)

      const focusTimer = setTimeout(() => {
        newCommentThreadInputRef.current?.focus()
      }, 200)

      return () => {
        clearTimeout(activeTimer)
        clearTimeout(focusTimer)
      }
    }

    setNewCommentThreadActive(false)

    return undefined
  }, [newCommentThread])

  return (
    <>
      {/*-- empty --*/}
      {commentThreads.length === 0 && !newCommentThread ? (
        <EmptyPlaceholder
          actions={
            onAddComment ? (
              <Button onClick={onAddComment} size="small" variant="outlined">
                {t('analyses', 'add_comment')}
              </Button>
            ) : undefined
          }
          background={true}
          body={noCommentsBody ?? t('key_metrics', 'no_comments_body')}
          size="small"
          title={t('key_metrics', 'no_comments_title')}
        />
      ) : null}

      {/*-- new --*/}
      {newCommentThread ? (
        <CommentThread
          active={newCommentThreadActive}
          analysisId={analysisId}
          data={newCommentThread ?? undefined}
          inputRef={newCommentThreadInputRef}
          keyMetricId={keyMetricId}
          widgetId={widgetId}
          {...CommentThreadProps}
          onCreateComment={onCreateComment}
          variant="create"
        />
      ) : null}

      {/*-- add comment button (when threads exist) --*/}
      {onAddComment && commentThreads.length > 0 && !newCommentThread ? (
        <Button
          onClick={onAddComment}
          size="small"
          startIcon={<Icon name="Add" />}
          sx={{ mt: 1 }}
          variant="text"
        >
          {t('analyses', 'add_comment')}
        </Button>
      ) : null}

      {/*-- existing --*/}
      {commentThreads.map((thread) => (
        <CommentThread
          active={focusedCommentThreadId === thread.id}
          analysisId={analysisId}
          id={thread.id}
          key={thread.id}
          keyMetricId={keyMetricId}
          widgetId={widgetId}
          onCreateComment={onCreateComment}
          setFocused={
            setFocusedCommentThreadId && setNewCommentThread
              ? (value) => {
                  if (value) {
                    setFocusedCommentThreadId(thread.id)
                    setNewCommentThread(null)
                  } else {
                    setFocusedCommentThreadId(null)
                  }
                }
              : undefined
          }
          variant="reply"
          {...CommentThreadProps}
        />
      ))}
    </>
  )
}

export default CommentThreads
